import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Component from '../../components/Component';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  buildSuperblogJson,
  downloadPostsJson,
  fetchSourceTypes,
  getHtmlPreviewFromPayload,
  getPreviewFromPayload,
  patchSuperblogBlog,
  searchCompetitions,
  searchSources,
  uploadSuperblogImage,
} from '../../services/superblogApi';

const SUPERBLOG_DASHBOARD_URL = 'https://write.superblog.ai/sites/dugoutlive/setup';

const FALLBACK_SOURCE_TYPES = [
  {
    id: 'curated_article',
    label: 'Curated article',
    whenToUse: 'Article already generated in Curated Articles admin',
  },
  {
    id: 'external_links',
    label: 'News links',
    whenToUse: 'Paste 1–3 Cricbuzz / ESPN / etc. URLs — AI writes blog',
  },
  {
    id: 'match',
    label: 'Match',
    whenToUse: 'Pick match — match summary + fan topics + Dugout posts',
  },
  {
    id: 'dugout_posts',
    label: 'Dugout posts',
    whenToUse: 'Pick fan posts — each links back to app',
  },
];

const FALLBACK_TONES = [
  { id: 'auto', label: 'Auto (default)', isDefault: true },
  { id: 'dominant_win', label: 'Dominant win' },
  { id: 'narrow_chase', label: 'Narrow chase' },
  { id: 'collapse', label: 'Collapse' },
  { id: 'milestone', label: 'Milestone' },
  { id: 'dead_rubber', label: 'Dead rubber' },
  { id: 'custom', label: 'Custom tone' },
];

const TONE_PRESETS = new Set([
  'dominant_win',
  'narrow_chase',
  'collapse',
  'milestone',
  'dead_rubber',
]);

function catalogueOrFallback(list, fallback) {
  return Array.isArray(list) && list.length ? list : fallback;
}

function rowId(row) {
  return row?.id ?? row?.value ?? '';
}

function defaultRowId(rows, fallbackId, defaultsValue) {
  const flagged = (rows || []).find((row) => row.isDefault);
  const id = rowId(flagged) || defaultsValue || fallbackId;
  return id == null || id === '' ? fallbackId : String(id);
}

function competitionIdOf(item) {
  if (!item) return '';
  const id = item.competitionId ?? item.cid ?? item.competition_id ?? item.id;
  return id == null || id === '' ? '' : String(id);
}

function matchIdOf(item) {
  if (!item) return '';
  const id = item.matchId ?? item.match_id ?? item.id;
  return id == null || id === '' ? '' : String(id);
}

/** Fan post id from the picker. This is the value sent as postIds. */
function postIdOf(item) {
  if (!item) return '';
  const id = item.id;
  return id == null || id === '' ? '' : String(id);
}

function articleIdOf(item) {
  if (!item) return '';
  const id = item.id ?? item.curatedArticleId;
  return id == null || id === '' ? '' : String(id);
}

function sourceOptionKey(type, item) {
  if (!item) return '';
  if (type === 'match') return matchIdOf(item);
  if (type === 'curated_article') return articleIdOf(item);
  if (type === 'competition') return competitionIdOf(item);
  return postIdOf(item);
}

function firstText(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function previewBody(text) {
  const flat = String(text || '').replace(/\s+/g, ' ').trim();
  if (!flat) return '';
  if (flat.length <= 80) return flat;
  return `${flat.slice(0, 80)}…`;
}

function dugoutPostLabel(item) {
  const provided = firstText(item.label);
  if (provided) return provided;
  const when = firstText(item.created_at, item.createdAt);
  const body = previewBody(firstText(item.content, item.body));
  if (when && body) return `${when} · ${body}`;
  return body || when || 'Dugout post';
}

function postKind(item) {
  if (item?.kind === 'image' || item?.kind === 'text') return item.kind;
  return firstText(item?.media_url, item?.mediaUrl) ? 'image' : 'text';
}

function postKindLabel(item) {
  return postKind(item) === 'image' ? 'Image' : 'Text';
}

function postsOfKind(list, kind) {
  return (Array.isArray(list) ? list : []).filter((item) => postKind(item) === kind);
}

function matchFallbackLabel(item) {
  const home = firstText(item.home_team_full_name, item.homeTeamFullName);
  const away = firstText(item.away_team_full_name, item.awayTeamFullName);
  const teams = home && away ? `${home} vs ${away}` : (home || away);
  const year = firstText(item.year, item.season, item.seasonYear);
  const competition = firstText(
    item.competitionName,
    item.competition_name,
    item.seriesName,
    item.series_name,
  );
  const fixture = firstText(item.matchName, item.match_name);
  const parts = [year, competition, teams, fixture].filter(Boolean);
  return parts.length ? parts.join('-') : 'Untitled match';
}

function sourceOptionLabel(type, item) {
  if (!item) return '';
  if (type === 'dugout_posts') return dugoutPostLabel(item);
  if (item.label) return String(item.label);
  if (type === 'match') return matchFallbackLabel(item);
  if (type === 'curated_article') {
    const id = articleIdOf(item);
    const title = item.title ?? item.headline ?? '';
    return title ? `${id} — ${title}` : id;
  }
  if (type === 'competition') {
    const abbr = firstText(item.abbr, item.abbreviation);
    const title = firstText(item.title, item.name);
    if (abbr && title) return `${abbr} — ${title}`;
    return title || competitionIdOf(item);
  }
  return item.title || String(item.id ?? '');
}

function AsyncPicker({
  label,
  value,
  onChange,
  multiple = false,
  disabled = false,
  placeholder,
  loadOptions,
  optionType,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const timer = useRef(null);

  const fetchOptions = useCallback(async (q = '') => {
    if (disabled) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const list = await loadOptions(q);
      setOptions(Array.isArray(list) ? list : []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [disabled, loadOptions]);

  useEffect(() => {
    fetchOptions('');
  }, [fetchOptions]);

  const handleInput = useCallback((_, next) => {
    setInputVal(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchOptions(next), 400);
  }, [fetchOptions]);

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      options={options}
      loading={loading}
      value={multiple ? (value || []) : value}
      inputValue={inputVal}
      onInputChange={handleInput}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(item) => sourceOptionLabel(optionType, item)}
      isOptionEqualToValue={(a, b) => sourceOptionKey(optionType, a) === sourceOptionKey(optionType, b)}
      filterOptions={(opts) => opts}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={placeholder || (multiple ? 'Search and select…' : 'Search…')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, item) => {
        const thumb = optionType === 'dugout_posts'
          ? firstText(item.thumbnail_url, item.media_url)
          : '';
        return (
          <li {...props} key={sourceOptionKey(optionType, item) || sourceOptionLabel(optionType, item)}>
            <Stack direction="row" spacing={1} alignItems="center">
              {thumb ? (
                <Box
                  component="img"
                  src={thumb}
                  alt=""
                  sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5 }}
                />
              ) : null}
              <Box>
                {optionType === 'dugout_posts' && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    <Box component="span">{postKindLabel(item)}</Box>
                    {item.user?.name ? ` · ${item.user.name}` : ''}
                  </Typography>
                )}
                <Typography variant="body2">{sourceOptionLabel(optionType, item)}</Typography>
              </Box>
            </Stack>
          </li>
        );
      }}
    />
  );
}

function parseUrls(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function numericIds(items, readId) {
  return (items || [])
    .map((item) => Number(readId(item)))
    .filter((id) => Number.isFinite(id) && id > 0);
}

/** posts[0].id is the integer saved by generate. A slug is not this id. */
function blogIdOf(payload) {
  const id = Number(payload?.posts?.[0]?.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return id;
}

function toneResolutionLabel(toneResolution) {
  if (!toneResolution?.resolvedTone) return '';
  if (toneResolution.mode === 'auto_inferred') {
    return `Resolved tone: ${toneResolution.resolvedTone} · auto`;
  }
  return `Resolved tone: ${toneResolution.resolvedTone}`;
}

/** htmlPreview, or the HTML post body when isMarkdown is false. */
function previewDocument(payload) {
  const html = getHtmlPreviewFromPayload(payload);
  if (html) return html;
  const post = payload?.posts?.[0] || payload?.post;
  if (!post || post.isMarkdown === true) return '';
  const content = typeof post.content === 'string' ? post.content.trim() : '';
  if (!content || post.isMarkdown !== false) return '';
  return content;
}

function SuperblogBuilder() {
  const [sourceTypes, setSourceTypes] = useState([]);
  const [tones, setTones] = useState(FALLBACK_TONES);
  const [sourceType, setSourceType] = useState('');
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [typesError, setTypesError] = useState('');

  const [curatedArticle, setCuratedArticle] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [match, setMatch] = useState(null);
  const [dugoutPosts, setDugoutPosts] = useState([]);
  const [extraPosts, setExtraPosts] = useState([]);
  const [imagePosts, setImagePosts] = useState([]);
  const [urlsText, setUrlsText] = useState('');
  const [includeDugoutPosts, setIncludeDugoutPosts] = useState(true);

  const [tone, setTone] = useState('auto');
  const [customTone, setCustomTone] = useState('');
  const [writingInstructions, setWritingInstructions] = useState('');
  const [optionalTitle, setOptionalTitle] = useState('');

  const [building, setBuilding] = useState(false);
  const [savingToc, setSavingToc] = useState(false);
  const [savingImages, setSavingImages] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [imageDraft, setImageDraft] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState(null);
  const [blogId, setBlogId] = useState(null);
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const [savedEdit, setSavedEdit] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTypes(true);
      setTypesError('');
      try {
        const data = await fetchSourceTypes();
        if (cancelled) return;
        const types = catalogueOrFallback(data.sourceTypes ?? data.types, FALLBACK_SOURCE_TYPES);
        const toneRows = catalogueOrFallback(data.tones, FALLBACK_TONES);
        setSourceTypes(types);
        setTones(toneRows);
        setTone(defaultRowId(toneRows, 'auto', data.defaults?.tone));
        setSourceType((current) => current || (rowId(types[0]) || ''));
      } catch (e) {
        if (!cancelled) {
          setTypesError(e.message);
          setSourceTypes(FALLBACK_SOURCE_TYPES);
          setTones(FALLBACK_TONES);
          setTone('auto');
          setSourceType((current) => current || FALLBACK_SOURCE_TYPES[0].id);
        }
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedTypeMeta = useMemo(
    () => sourceTypes.find((t) => rowId(t) === sourceType),
    [sourceTypes, sourceType],
  );

  const selectedCompetitionId = competitionIdOf(competition);
  const selectedMatchId = matchIdOf(match);

  const loadCompetitions = useCallback(
    (q) => searchCompetitions({ q, limit: 20 }),
    [],
  );

  const loadCuratedArticles = useCallback(
    (q) => searchSources({ type: 'curated_article', q, limit: 20, offset: 0 }),
    [],
  );

  const loadMatches = useCallback(
    (q) => searchSources({
      type: 'match',
      q,
      limit: 80,
      offset: 0,
      cid: selectedCompetitionId,
    }),
    [selectedCompetitionId],
  );

  const loadTextPosts = useCallback(
    async (q) => {
      if (sourceType === 'match' && (!selectedCompetitionId || !selectedMatchId)) return [];
      const scope = sourceType === 'match'
        ? { cid: selectedCompetitionId, mid: selectedMatchId }
        : {};
      return postsOfKind(
        await searchSources({ type: 'dugout_posts', q, limit: 20, offset: 0, kind: 'text', ...scope }),
        'text',
      );
    },
    [sourceType, selectedCompetitionId, selectedMatchId],
  );

  const loadImagePosts = useCallback(
    async (q) => {
      if (sourceType === 'match' && (!selectedCompetitionId || !selectedMatchId)) return [];
      const scope = sourceType === 'match'
        ? { cid: selectedCompetitionId, mid: selectedMatchId }
        : {};
      return postsOfKind(
        await searchSources({ type: 'dugout_posts', q, limit: 20, offset: 0, kind: 'image', ...scope }),
        'image',
      );
    },
    [sourceType, selectedCompetitionId, selectedMatchId],
  );

  const sourceFingerprint = useMemo(() => JSON.stringify({
    sourceType,
    curatedArticleId: articleIdOf(curatedArticle),
    urls: parseUrls(urlsText),
    competitionId: selectedCompetitionId,
    matchId: matchIdOf(match),
    postIds: sourceType === 'dugout_posts'
      ? numericIds(dugoutPosts, postIdOf)
      : numericIds(extraPosts, postIdOf),
    imagePostIds: numericIds(imagePosts, postIdOf),
    includeDugoutPosts: sourceType === 'dugout_posts' ? true : includeDugoutPosts,
  }), [
    sourceType,
    curatedArticle,
    urlsText,
    selectedCompetitionId,
    match,
    dugoutPosts,
    extraPosts,
    imagePosts,
    includeDugoutPosts,
  ]);

  const resetSourceFields = () => {
    setCuratedArticle(null);
    setCompetition(null);
    setMatch(null);
    setDugoutPosts([]);
    setExtraPosts([]);
    setImagePosts([]);
    setUrlsText('');
    setIncludeDugoutPosts(true);
    setResult(null);
    setBlogId(null);
    setSavedFingerprint('');
    setSavedEdit('');
    setError('');
    setSuccess('');
  };

  const handleSourceTypeChange = (next) => {
    setSourceType(next);
    resetSourceFields();
  };

  const generationFields = () => {
    const fields = { tone: tone || 'auto' };
    if (fields.tone === 'custom') {
      const text = customTone.trim();
      if (!text) throw new Error('Enter a custom tone.');
      fields.customTone = text;
    }
    if (writingInstructions.trim()) {
      fields.writingInstructions = writingInstructions.trim();
    }
    return fields;
  };

  const attachFanSelection = (payload, textItems, required) => {
    const postIds = numericIds(textItems, postIdOf);
    const imagePostIds = numericIds(imagePosts, postIdOf);
    if (required && !postIds.length && !imagePostIds.length) {
      throw new Error('Select at least one text post or image post');
    }
    if (postIds.length) payload.postIds = postIds;
    if (imagePostIds.length) payload.imagePostIds = imagePostIds;
  };

  const buildPayload = () => {
    const payload = { sourceType };
    const options = generationFields();
    const title = optionalTitle.trim();
    if (title) payload.optionalTitle = title;

    if (sourceType === 'curated_article') {
      const id = articleIdOf(curatedArticle);
      if (!id) throw new Error('Select a curated article');
      payload.curatedArticleId = Number(id);
      options.includeDugoutPosts = includeDugoutPosts;
      if (includeDugoutPosts) attachFanSelection(payload, extraPosts, false);
      payload.options = options;
      return payload;
    }

    if (sourceType === 'external_links') {
      const urls = parseUrls(urlsText);
      if (urls.length < 1 || urls.length > 3) {
        throw new Error('Enter 1–3 source URLs (one per line)');
      }
      payload.sourceUrls = urls;
      options.includeDugoutPosts = includeDugoutPosts;
      if (includeDugoutPosts) attachFanSelection(payload, extraPosts, false);
      payload.options = options;
      return payload;
    }

    if (sourceType === 'match') {
      if (!selectedCompetitionId) throw new Error('Select a competition first');
      const matchId = matchIdOf(match);
      if (!matchId) throw new Error('Select a match');
      payload.matchId = Number(matchId);
      options.includeDugoutPosts = includeDugoutPosts;
      if (includeDugoutPosts) attachFanSelection(payload, extraPosts, false);
      payload.options = options;
      return payload;
    }

    if (sourceType === 'dugout_posts') {
      attachFanSelection(payload, dugoutPosts, true);
      payload.options = options;
      return payload;
    }

    throw new Error('Unknown source type');
  };

  const currentEdit = () => ({
    tone: tone || 'auto',
    customTone: tone === 'custom' ? customTone.trim() : '',
    writing: writingInstructions.trim(),
    title: optionalTitle.trim(),
  });

  const editAfterResolution = (toneResolution) => {
    const next = currentEdit();
    if (!toneResolution || toneResolution.mode === 'auto_inferred') {
      next.tone = 'auto';
      next.customTone = '';
      return next;
    }
    if (toneResolution.mode === 'specified') {
      if (TONE_PRESETS.has(toneResolution.resolvedTone)) {
        next.tone = toneResolution.resolvedTone;
        next.customTone = '';
      } else {
        next.tone = 'custom';
        next.customTone = firstText(toneResolution.inputSpecified, toneResolution.resolvedTone);
      }
    }
    return next;
  };

  const buildPatchBody = () => {
    const next = currentEdit();
    const previous = savedEdit ? JSON.parse(savedEdit) : null;
    const toneChanged = !previous || previous.tone !== next.tone || previous.customTone !== next.customTone;
    const writingChanged = !previous || previous.writing !== next.writing;
    const titleChanged = Boolean(next.title) && (!previous || previous.title !== next.title);
    const body = {};
    if (toneChanged) {
      body.tone = next.tone;
      if (next.tone === 'custom') {
        if (!next.customTone) throw new Error('Enter a custom tone.');
        body.customTone = next.customTone;
      }
    }
    if (writingChanged && next.writing) body.writingInstructions = next.writing;
    if (titleChanged) body.title = next.title;
    if (!Object.keys(body).length) {
      throw new Error('Change the tone, writing notes, or title to update this blog.');
    }
    return body;
  };

  const applyToneResolution = (toneResolution) => {
    if (!toneResolution) return;
    if (toneResolution.mode === 'auto_inferred') {
      setTone('auto');
      return;
    }
    if (toneResolution.mode !== 'specified') return;
    if (TONE_PRESETS.has(toneResolution.resolvedTone)) {
      setTone(toneResolution.resolvedTone);
      return;
    }
    setTone('custom');
    const specified = firstText(toneResolution.inputSpecified, toneResolution.resolvedTone);
    if (specified) setCustomTone(specified);
  };

  const handleApiError = (err) => {
    setError(err?.message || 'Request failed');
  };

  const applyBuildResult = (apiBody, buildResult, fingerprint) => {
    const posts = buildResult?.posts;
    if (!Array.isArray(posts) || posts.length === 0) {
      throw new Error('Generate did not return a posts file');
    }
    const id = blogIdOf(buildResult);
    setResult(buildResult);
    setBlogId(id);
    setSavedFingerprint(id == null ? '' : fingerprint);
    setSavedEdit(JSON.stringify(editAfterResolution(buildResult.toneResolution)));
    applyToneResolution(buildResult.toneResolution);
    setSuccess(apiBody?.message || (id == null
      ? 'Superblog JSON ready. Download the posts file and import it.'
      : `Blog ${id} saved. Download uses this response.`));
  };

  const handleGenerate = async () => {
    setError('');
    setSuccess('');
    const creating = !blogId || sourceFingerprint !== savedFingerprint;
    let payload;
    let patchBody;
    try {
      if (creating) payload = buildPayload();
      else patchBody = buildPatchBody();
    } catch (e) {
      setError(e.message);
      return;
    }
    setBuilding(true);
    try {
      if (creating) {
        const { body, payload: buildResult } = await buildSuperblogJson(payload);
        applyBuildResult(body, buildResult, sourceFingerprint);
      } else {
        const { body, payload: buildResult } = await patchSuperblogBlog(blogId, patchBody);
        const id = blogIdOf(buildResult) ?? blogId;
        setResult(buildResult);
        setBlogId(id);
        setSavedEdit(JSON.stringify(editAfterResolution(buildResult.toneResolution)));
        applyToneResolution(buildResult.toneResolution);
        setSuccess(body.message || `Blog ${id} updated`);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setBuilding(false);
    }
  };

  const handleDownload = () => {
    const posts = result?.posts;
    if (!Array.isArray(posts) || posts.length === 0) {
      setError('Generate a blog before downloading.');
      return;
    }
    setError('');
    const id = posts[0]?.id ?? 'export';
    downloadPostsJson(posts, `superblog-${id}.json`);
    setSuccess(`Downloaded superblog-${id}.json`);
  };

  const savedImages = Array.isArray(result?.images)
    ? result.images.filter((url) => typeof url === 'string')
    : [];
  const savedImageKey = savedImages.join('\n');

  useEffect(() => {
    setImageDraft(savedImageKey ? savedImageKey.split('\n') : []);
  }, [savedImageKey]);

  const handleAutoToc = async (next) => {
    if (!blogId) return;
    setError('');
    setSavingToc(true);
    try {
      const { body, payload: buildResult } = await patchSuperblogBlog(blogId, { isAutoTOC: next });
      setResult(buildResult);
      setSuccess(body.message || `Blog ${blogId} updated`);
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingToc(false);
    }
  };

  const handleUploadImage = async (index, file) => {
    if (!file || !blogId) return;
    setError('');
    setUploadingImageIndex(index);
    try {
      const url = await uploadSuperblogImage(file);
      setImageDraft((current) => {
        const next = current.slice();
        next[index] = url;
        return next;
      });
      setSuccess('Image uploaded. Save images to put it in the article.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const handleSaveImages = async () => {
    if (!blogId) return;
    const next = imageDraft.map((url) => url.trim());
    if (next.length !== savedImages.length) {
      setError('Keep the same number of images.');
      return;
    }
    if (next.some((url) => !url.startsWith('https://'))) {
      setError('Each image must be an HTTPS URL.');
      return;
    }
    setError('');
    setSavingImages(true);
    try {
      const { body, payload: buildResult } = await patchSuperblogBlog(blogId, { images: next });
      setResult(buildResult);
      setSuccess(body.message || `Blog ${blogId} updated`);
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingImages(false);
    }
  };

  const moveImage = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= imageDraft.length) return;
    const next = imageDraft.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setImageDraft(next);
  };

  const htmlPreview = previewDocument(result);
  const markdownPreview = htmlPreview ? '' : getPreviewFromPayload(result);
  const toneLine = toneResolutionLabel(result?.toneResolution);
  const templateUsed = result
    ? (result.articleTemplate?.label || 'Universal template')
    : '';
  const autoToc = result?.posts?.[0]?.isAutoTOC === 'no' ? 'no' : 'yes';
  const postsReady = Array.isArray(result?.posts) && result.posts.length > 0;
  const instructions = result?.importInstructions ?? result?.import_instructions ?? [
    'Open Superblog → Dashboard → Data → Import from JSON',
    'Upload the downloaded file (or paste the posts array)',
    'Click Deploy — your blog post goes live on dugoutlive.com/blog',
  ];
  const busy = building || savingToc || savingImages || uploadingImageIndex != null || loadingTypes;
  const imagesChanged = imageDraft.map((url) => url.trim()).join('\n') !== savedImageKey;
  const sourceChanged = Boolean(blogId) && sourceFingerprint !== savedFingerprint;

  const matchPostsReady = sourceType !== 'match' || Boolean(selectedCompetitionId && selectedMatchId);
  const postCopy = sourceType === 'match'
    ? {
      story: 'Upload a photo on the saved blog when the story needs one.',
      text: 'Selected posts become the Fan Buzz boxes, up to 3. With none selected, the top posts from this match are used.',
      image: 'A picture post is a Fan Buzz box. Its photo stays inside the box, and the box links to the post on Dugout.',
    }
    : sourceType === 'external_links'
      ? {
        story: 'Story photos come from the news links. Upload a photo on the saved blog to fill an empty slot.',
        text: 'Selected posts become the Fan Buzz boxes, up to 3.',
        image: 'A picture post is a Fan Buzz box. Its photo stays inside the box, and the box links to the post on Dugout.',
      }
      : sourceType === 'dugout_posts'
        ? {
          story: '',
          text: 'Selected text posts become the Fan Buzz boxes.',
          image: 'Photos from these posts are the story photos.',
        }
        : {
          story: 'Story photos come from the article cover and brief images. Upload a photo on the saved blog to fill an empty slot.',
          text: 'Selected posts become the Fan Buzz boxes, up to 3.',
          image: 'A picture post is a Fan Buzz box. Its photo stays inside the box, and the box links to the post on Dugout.',
        };

  const fanPickers = (textLabel, textValue, onTextChange) => (
    <Stack spacing={2}>
      {postCopy.story ? (
        <Typography variant="caption" color="text.secondary">
          {postCopy.story}
        </Typography>
      ) : null}
      <Stack spacing={0.5}>
        <AsyncPicker
          optionType="dugout_posts"
          label={textLabel}
          value={textValue}
          onChange={onTextChange}
          multiple
          disabled={!matchPostsReady}
          placeholder={matchPostsReady ? 'Search and select…' : 'Select a competition and a match first'}
          loadOptions={loadTextPosts}
        />
        <Typography variant="caption" color="text.secondary">
          {postCopy.text}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        <AsyncPicker
          optionType="dugout_posts"
          label="Image posts"
          value={imagePosts}
          onChange={setImagePosts}
          multiple
          disabled={!matchPostsReady}
          placeholder={matchPostsReady ? 'Search and select…' : 'Select a competition and a match first'}
          loadOptions={loadImagePosts}
        />
        <Typography variant="caption" color="text.secondary">
          {postCopy.image}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ py: 2, maxWidth: 960 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Blog Generator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate once, then download the posts file from that response. Tone and writing notes update the same blog. The layout stays the universal template. Import the file in Superblog → Data → Import from JSON.
            </Typography>

            {typesError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Could not load source types ({typesError}). Using the built-in catalogues.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="superblog-source-type">Source type</InputLabel>
                <Select
                  labelId="superblog-source-type"
                  label="Source type"
                  value={sourceType}
                  onChange={(e) => handleSourceTypeChange(e.target.value)}
                  disabled={loadingTypes}
                >
                  {sourceTypes.map((t) => {
                    const id = rowId(t);
                    return (
                      <MenuItem key={id} value={id}>
                        {t.label ?? t.name ?? id}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {(selectedTypeMeta?.whenToUse ?? selectedTypeMeta?.description) && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedTypeMeta.whenToUse ?? selectedTypeMeta.description}
                </Typography>
              )}

              {sourceType === 'curated_article' && (
                <Stack spacing={2}>
                  <AsyncPicker
                    optionType="curated_article"
                    label="Curated article"
                    value={curatedArticle}
                    onChange={setCuratedArticle}
                    loadOptions={loadCuratedArticles}
                    placeholder="Search articles…"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={includeDugoutPosts}
                        onChange={(e) => setIncludeDugoutPosts(e.target.checked)}
                      />
                    )}
                    label="Include Dugout posts"
                  />
                  {includeDugoutPosts && fanPickers('Text posts', extraPosts, setExtraPosts)}
                </Stack>
              )}

              {sourceType === 'external_links' && (
                <Stack spacing={2}>
                  <TextField
                    label="Source URLs (1–3, one per line)"
                    multiline
                    minRows={3}
                    size="small"
                    value={urlsText}
                    onChange={(e) => setUrlsText(e.target.value)}
                    placeholder={'https://www.espncricinfo.com/story/...\nhttps://www.cricbuzz.com/...'}
                    fullWidth
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={includeDugoutPosts}
                        onChange={(e) => setIncludeDugoutPosts(e.target.checked)}
                      />
                    )}
                    label="Include Dugout posts"
                  />
                  {includeDugoutPosts && fanPickers('Text posts', extraPosts, setExtraPosts)}
                </Stack>
              )}

              {sourceType === 'match' && (
                <Stack spacing={2}>
                  <AsyncPicker
                    optionType="competition"
                    label="Competition / league"
                    value={competition}
                    onChange={(next) => {
                      setCompetition(next);
                      setMatch(null);
                      setExtraPosts([]);
                      setImagePosts([]);
                    }}
                    loadOptions={loadCompetitions}
                    placeholder="Search leagues…"
                  />
                  <AsyncPicker
                    optionType="match"
                    label="Match"
                    value={match}
                    onChange={(next) => {
                      setMatch(next);
                      setExtraPosts([]);
                      setImagePosts([]);
                    }}
                    loadOptions={loadMatches}
                    disabled={!selectedCompetitionId}
                    placeholder={selectedCompetitionId ? 'Search matches…' : 'Select a league first'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Matches stay in competition order. A series looks like 2026-Australia tour of South Africa-Australia vs South Africa-2nd ODI. A tournament looks like 2026-ICC World Cup-India vs England-Match 12.
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={includeDugoutPosts}
                        onChange={(e) => setIncludeDugoutPosts(e.target.checked)}
                      />
                    )}
                    label="Include Dugout posts"
                  />
                  {includeDugoutPosts && fanPickers('Text posts', extraPosts, setExtraPosts)}
                </Stack>
              )}

              {sourceType === 'dugout_posts' && (
                fanPickers('Text posts', dugoutPosts, setDugoutPosts)
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2.5 }} alignItems="flex-start">
                <FormControl size="small" sx={{ minWidth: 260, flex: 1 }} disabled={loadingTypes}>
                  <InputLabel id="superblog-tone">Tone</InputLabel>
                  <Select
                    labelId="superblog-tone"
                    label="Tone"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                  >
                    {tones.map((row) => {
                      const id = rowId(row);
                      return (
                        <MenuItem key={id} value={id}>{row.label ?? id}</MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {tone === 'custom' && (
                  <TextField
                    label="Custom tone"
                    size="small"
                    required
                    value={customTone}
                    onChange={(e) => setCustomTone(e.target.value)}
                    placeholder="critical of selection"
                    helperText="Required. For example: critical of selection, celebratory, aggressive banter."
                    sx={{ flex: 1 }}
                    fullWidth
                  />
                )}
              </Stack>

              <TextField
                label="Writing notes (optional)"
                size="small"
                multiline
                minRows={2}
                value={writingInstructions}
                onChange={(e) => setWritingInstructions(e.target.value)}
                placeholder="Angle or length"
                helperText="Optional angle or length. This does not change the layout."
                fullWidth
                sx={{ mt: 2 }}
              />
              <TextField
                label="Title (optional)"
                size="small"
                value={optionalTitle}
                onChange={(e) => setOptionalTitle(e.target.value)}
                helperText="Used verbatim. Leave blank and the model writes a keyword-first title of 8 to 12 words."
                fullWidth
                sx={{ mt: 2 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }} flexWrap="wrap" alignItems="center">
                <Button
                  variant="contained"
                  startIcon={building ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleGenerate}
                  disabled={busy || !sourceType}
                >
                  Generate
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={busy || !postsReady}
                >
                  Download JSON
                </Button>
                {blogId && (
                  <Typography variant="caption" color="text.secondary">
                    {sourceChanged
                      ? 'Source changed. Generate saves a new blog.'
                      : `Tone, writing notes, and title update blog ${blogId}.`}
                  </Typography>
                )}
              </Stack>
            </Paper>

            {(toneLine || templateUsed || blogId) && (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {blogId && (
                  <Typography variant="body2">Saved blog {blogId}</Typography>
                )}
                {toneLine && (
                  <Typography variant="body2">{toneLine}</Typography>
                )}
                {templateUsed && (
                  <Typography variant="body2">Template used: {templateUsed}</Typography>
                )}
                {blogId && (
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={autoToc === 'yes'}
                        onChange={(e) => handleAutoToc(e.target.checked ? 'yes' : 'no')}
                        disabled={busy}
                      />
                    )}
                    label="Article table of contents"
                  />
                )}
              </Stack>
            )}

            {htmlPreview ? (
              <Box sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                  <Box
                    component="iframe"
                    title="Superblog preview"
                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                    srcDoc={htmlPreview}
                    sx={{ display: 'block', width: '100%', minHeight: 480, border: 0 }}
                  />
                </Paper>
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  minHeight: 160,
                  bgcolor: 'grey.50',
                  fontFamily: markdownPreview ? 'monospace' : 'inherit',
                  fontSize: markdownPreview ? 13 : 'inherit',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {markdownPreview || (
                  <Typography variant="body2" color="text.secondary">
                    Generate a blog to preview it here.
                  </Typography>
                )}
              </Paper>
            )}

            {imageDraft.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Images
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  These are the story photos, in article order. Upload a file or paste an HTTPS link to fill a slot, then save. Saving rewrites the article and does not generate a new blog.
                </Typography>
                <Stack spacing={1.5}>
                  {imageDraft.map((url, index) => (
                    <Stack key={`image-${index}`} direction="row" spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={url}
                        alt={`Image ${index + 1}`}
                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                      />
                      <TextField
                        label={`Image ${index + 1}`}
                        size="small"
                        value={url}
                        onChange={(e) => {
                          const next = imageDraft.slice();
                          next[index] = e.target.value;
                          setImageDraft(next);
                        }}
                        disabled={!blogId || busy}
                        fullWidth
                      />
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        disabled={!blogId || busy || uploadingImageIndex != null}
                        startIcon={uploadingImageIndex === index ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                        sx={{ flexShrink: 0 }}
                      >
                        Upload
                        <input
                          hidden
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (file) handleUploadImage(index, file);
                          }}
                        />
                      </Button>
                      <IconButton
                        aria-label={`Move image ${index + 1} up`}
                        size="small"
                        disabled={!blogId || busy || index === 0}
                        onClick={() => moveImage(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label={`Move image ${index + 1} down`}
                        size="small"
                        disabled={!blogId || busy || index === imageDraft.length - 1}
                        onClick={() => moveImage(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                {blogId && (
                  <Button
                    sx={{ mt: 1.5 }}
                    variant="outlined"
                    onClick={handleSaveImages}
                    disabled={busy || !imagesChanged}
                  >
                    Save images
                  </Button>
                )}
              </Box>
            )}

            <Alert severity="info" icon={<OpenInNewIcon fontSize="inherit" />}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Instructions: Import in Superblog → Data → JSON
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {instructions.map((line) => (
                  <li key={line}>
                    <Typography variant="body2">{line}</Typography>
                  </li>
                ))}
              </Box>
              <Link
                href={SUPERBLOG_DASHBOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 1, display: 'inline-block' }}
              >
                Open Superblog dashboard →
              </Link>
            </Alert>
          </Box>
        </div>
      </div>
    </Component>
  );
}

export default SuperblogBuilder;
